import type { ActionMeta, FieldMeta, FieldType, ResourceMeta } from './types';

// Fallback ResourceMeta for the existing finance resources, derived from web/src/types.ts and
// the backend ResourceDefs (src/api/resources/*). Used only when GET /v1/meta/resources/:name
// is not served yet (404 / network) so every engine screen renders today. The real meta
// replaces this byte-for-byte in shape.

type F = Partial<FieldMeta> & { name: string; type: FieldType };

function field(f: F): FieldMeta {
  return {
    label: humanize(f.name),
    required: false,
    readOnly: false,
    nullable: true,
    filterable: true,
    sortable: true,
    searchable: false,
    inCreate: false,
    inUpdate: false,
    inList: true,
    inDetail: true,
    ...f,
  };
}

export function humanize(name: string): string {
  return name
    .replace(/_minor$/, '')
    .replace(/_id$/, '')
    .replace(/_/g, ' ')
    .replace(/\bqty\b/, 'quantity')
    .replace(/^\w/, (c) => c.toUpperCase());
}

const sys = (): FieldMeta[] => [
  field({ name: 'id', type: 'uuid', label: 'ID', readOnly: true, inList: false, inDetail: false, filterable: false, sortable: false }),
  field({ name: 'created_at', type: 'datetime', label: 'Created', readOnly: true, inList: false, order: 900 }),
  field({ name: 'updated_at', type: 'datetime', label: 'Updated', readOnly: true, inList: false, order: 901 }),
];

const money = (name: string, label: string, extra: Partial<FieldMeta> = {}) =>
  field({ name, type: 'money', label, currencyField: 'currency', align: 'right', ...extra });

const enumOf = (values: [string, string, string?][]) => values.map(([value, label, tone]) => ({ value, label, ...(tone ? { tone } : {}) }));

const act = (a: Partial<ActionMeta> & { name: string; label: string }): ActionMeta => ({
  method: 'POST',
  path: a.scope === 'collection' ? a.name : `:id/${a.name}`,
  scope: 'item',
  tone: 'default',
  ...a,
});

const base = (m: Partial<ResourceMeta> & Pick<ResourceMeta, 'name' | 'table' | 'label' | 'labelPlural' | 'fields'>): ResourceMeta => ({
  module: 'finance',
  capabilities: { list: true, get: true, create: false, update: false, remove: false },
  idField: 'id',
  displayField: 'name',
  defaultSort: '-created_at',
  searchFields: ['name'],
  actions: [],
  permissions: [],
  icon: 'file-text',
  ...m,
});

export const FALLBACK_META: Record<string, ResourceMeta> = {
  clients: base({
    name: 'clients',
    table: 'client',
    label: 'Client',
    labelPlural: 'Clients',
    icon: 'building',
    capabilities: { list: true, get: true, create: true, update: true, remove: false },
    searchFields: ['name'],
    defaultSort: 'name',
    fields: [
      field({ name: 'name', type: 'text', label: 'Name', required: true, nullable: false, searchable: true, inCreate: true, inUpdate: true, order: 1 }),
      field({ name: 'status', type: 'enum', label: 'Status', nullable: false, inUpdate: true, order: 2, default: 'active', enum: enumOf([['active', 'Active', 'pos'], ['inactive', 'Inactive', 'muted'], ['prospect', 'Prospect', 'info']]) }),
      field({ name: 'currency', type: 'text', label: 'Currency', inCreate: true, order: 3, help: 'ISO 4217 code, e.g. USD', width: 100 }),
      field({ name: 'timezone', type: 'text', label: 'Timezone', inCreate: true, inUpdate: true, inList: false, order: 4 }),
      field({ name: 'billing_party_id', type: 'uuid', label: 'Billing party', readOnly: true, inList: false, inDetail: false, filterable: false, sortable: false }),
      field({ name: 'account_manager_tech_id', type: 'relation', label: 'Account manager', inUpdate: true, inList: false, order: 5, relation: { resource: 'technicians', displayField: 'name' } }),
      ...sys(),
    ],
  }),

  technicians: base({
    name: 'technicians',
    table: 'technician',
    label: 'Technician',
    labelPlural: 'Technicians',
    icon: 'wrench',
    capabilities: { list: true, get: true, create: true, update: true, remove: false },
    defaultSort: 'name',
    fields: [
      field({ name: 'name', type: 'text', label: 'Name', required: true, nullable: false, searchable: true, inCreate: true, inUpdate: true, order: 1 }),
      field({ name: 'email', type: 'email', label: 'Email', searchable: true, inCreate: true, order: 2 }),
      field({ name: 'employment', type: 'enum', label: 'Employment', inCreate: true, order: 3, default: 'employee', enum: enumOf([['employee', 'Employee', 'accent'], ['contractor', 'Contractor', 'info']]) }),
      field({ name: 'status', type: 'enum', label: 'Status', nullable: false, inUpdate: true, order: 4, enum: enumOf([['active', 'Active', 'pos'], ['inactive', 'Inactive', 'muted']]) }),
      money('default_bill_rate_minor', 'Bill rate / h', { inCreate: true, inUpdate: true, order: 5, group: 'Rates', help: 'What they bill per hour' }),
      money('cost_rate_minor', 'Cost rate / h', { inCreate: true, inUpdate: true, order: 6, group: 'Rates', help: 'What they cost per hour (optional)' }),
      field({ name: 'weekly_capacity_minutes', type: 'duration', label: 'Weekly capacity', inCreate: true, inUpdate: true, inList: false, order: 7, group: 'Rates', align: 'right' }),
      field({ name: 'currency', type: 'text', label: 'Currency', readOnly: true, inList: false, order: 8 }),
      ...sys(),
    ],
    actions: [act({ name: 'cost-rates', label: 'Cost rate history', method: 'GET', scope: 'item' })],
  }),

  contracts: base({
    name: 'contracts',
    table: 'contract',
    label: 'Contract',
    labelPlural: 'Contracts',
    icon: 'file-check',
    capabilities: { list: true, get: true, create: true, update: true, remove: false },
    displayField: 'ref',
    searchFields: ['ref', 'client_name'],
    lifecycle: { field: 'status', steps: ['pending', 'active', 'expired'], terminal: ['cancelled'] },
    fields: [
      field({ name: 'client_name', type: 'text', label: 'Client', readOnly: true, searchable: true, sortable: false, filterable: false, order: 1 }),
      field({ name: 'billing_party_id', type: 'relation', label: 'Client', required: true, nullable: false, inCreate: true, inList: false, inDetail: false, order: 1, relation: { resource: 'clients', displayField: 'name', valueField: 'billing_party_id' } }),
      field({ name: 'ref', type: 'text', label: 'Reference', searchable: true, inCreate: true, inUpdate: true, order: 2, help: 'e.g. MSA-2026-01' }),
      field({ name: 'status', type: 'enum', label: 'Status', nullable: false, inUpdate: true, order: 3, enum: enumOf([['pending', 'Pending', 'warn'], ['active', 'Active', 'pos'], ['expired', 'Expired', 'muted'], ['cancelled', 'Cancelled', 'neg']]) }),
      field({ name: 'cycle', type: 'enum', label: 'Cycle', inCreate: true, order: 4, default: 'monthly', enum: enumOf([['weekly', 'Weekly'], ['monthly', 'Monthly'], ['quarterly', 'Quarterly'], ['biannual', 'Biannual'], ['annual', 'Annual'], ['oneoff', 'One-off'], ['custom', 'Custom']]) }),
      money('period_charge_minor', 'Period charge', { inCreate: true, inUpdate: true, order: 5 }),
      field({ name: 'currency', type: 'text', label: 'Currency', inCreate: true, inList: false, order: 6 }),
      field({ name: 'start_date', type: 'date', label: 'Start', inCreate: true, order: 7, inList: false }),
      field({ name: 'end_date', type: 'date', label: 'End', inCreate: true, inUpdate: true, order: 8, inList: false }),
      field({ name: 'next_invoice_date', type: 'date', label: 'Next invoice', inUpdate: true, order: 9 }),
      field({ name: 'client_id', type: 'relation', label: 'Client record', inCreate: true, inList: false, inDetail: false, order: 10, relation: { resource: 'clients', displayField: 'name' } }),
      ...sys(),
    ],
  }),

  invoices: base({
    name: 'invoices',
    table: 'invoice',
    label: 'Invoice',
    labelPlural: 'Invoices',
    icon: 'receipt',
    capabilities: { list: true, get: true, create: true, update: false, remove: false },
    displayField: 'number',
    searchFields: ['number', 'client_name'],
    lifecycle: { field: 'status', steps: ['draft', 'issued', 'frozen'], terminal: ['voided'] },
    fields: [
      field({ name: 'number', type: 'text', label: 'Number', readOnly: true, searchable: true, order: 1, width: 140 }),
      field({ name: 'client_name', type: 'text', label: 'Client', readOnly: true, searchable: true, sortable: false, filterable: false, order: 2 }),
      field({ name: 'billing_party_id', type: 'relation', label: 'Client', required: true, nullable: false, inCreate: true, inList: false, inDetail: false, order: 2, relation: { resource: 'clients', displayField: 'name', valueField: 'billing_party_id' } }),
      field({ name: 'kind', type: 'enum', label: 'Kind', readOnly: true, inList: false, order: 3, enum: enumOf([['invoice', 'Invoice', 'muted'], ['credit_note', 'Credit note', 'info']]) }),
      field({ name: 'status', type: 'enum', label: 'Status', readOnly: true, nullable: false, order: 4, enum: enumOf([['draft', 'Draft', 'muted'], ['issued', 'Issued', 'info'], ['frozen', 'Frozen', 'warn'], ['voided', 'Voided', 'neg']]) }),
      field({ name: 'issue_date', type: 'date', label: 'Issued', readOnly: true, order: 5 }),
      field({ name: 'due_date', type: 'date', label: 'Due', readOnly: true, order: 6, inList: false }),
      field({ name: 'total', type: 'money', label: 'Total', readOnly: true, widget: 'money-dto', align: 'right', order: 7, currencyField: 'currency' }),
      field({ name: 'outstanding', type: 'money', label: 'Outstanding', readOnly: true, widget: 'money-dto', align: 'right', order: 8, currencyField: 'currency', sortable: false, filterable: false }),
      field({ name: 'currency', type: 'text', label: 'Currency', inCreate: true, inList: false, order: 9 }),
      field({ name: 'contract_id', type: 'relation', label: 'Contract', inCreate: true, inList: false, order: 10, relation: { resource: 'contracts', displayField: 'ref' } }),
      field({ name: 'accounting_state', type: 'text', label: 'Accounting', readOnly: true, inList: false, order: 11 }),
      ...sys(),
    ],
    actions: [
      act({ name: 'issue', label: 'Issue', tone: 'primary', icon: 'send', when: { status: ['draft'] }, confirm: { title: 'Issue this invoice?', body: 'A number is assigned and the total is posted to accounts receivable. The document can no longer be edited.', confirmLabel: 'Issue' } }),
      act({ name: 'freeze', label: 'Freeze', when: { status: ['issued'] }, icon: 'lock' }),
      act({ name: 'void', label: 'Void', tone: 'danger', icon: 'ban', when: { status: ['issued', 'frozen'] }, confirm: { title: 'Void this invoice?', body: 'A reversing ledger entry is posted. This cannot be undone.', confirmLabel: 'Void invoice', reason: 'optional' } }),
      act({ name: 'credit-note', label: 'Credit note', icon: 'rotate-ccw', when: { status: ['issued', 'frozen'] }, confirm: { title: 'Create a credit note?', body: 'A credit note for the full amount is drafted against this invoice.', confirmLabel: 'Create credit note', reason: 'optional' } }),
      act({ name: 'from-time', label: 'Invoice from time', scope: 'collection', path: 'from-time', icon: 'clock' }),
    ],
  }),

  timesheets: base({
    name: 'timesheets',
    table: 'timesheet',
    label: 'Timesheet',
    labelPlural: 'Timesheets',
    icon: 'clock',
    capabilities: { list: true, get: true, create: true, update: false, remove: false },
    displayField: 'period_start',
    searchFields: [],
    defaultSort: '-period_start',
    lifecycle: { field: 'status', steps: ['open', 'submitted', 'approved', 'locked'] },
    fields: [
      field({ name: 'technician_id', type: 'relation', label: 'Technician', required: true, nullable: false, inCreate: true, order: 1, relation: { resource: 'technicians', displayField: 'name' } }),
      field({ name: 'period_start', type: 'date', label: 'Period start', required: true, nullable: false, inCreate: true, order: 2 }),
      field({ name: 'period_end', type: 'date', label: 'Period end', required: true, nullable: false, inCreate: true, order: 3 }),
      field({ name: 'status', type: 'enum', label: 'Status', readOnly: true, nullable: false, order: 4, enum: enumOf([['open', 'Open', 'info'], ['submitted', 'Submitted', 'warn'], ['approved', 'Approved', 'accent'], ['locked', 'Locked', 'pos']]) }),
      field({ name: 'total_minutes', type: 'duration', label: 'Hours', readOnly: true, align: 'right', order: 5, sortable: false, filterable: false }),
      field({ name: 'locked_at', type: 'datetime', label: 'Locked', readOnly: true, order: 6 }),
      field({ name: 'locked_by', type: 'text', label: 'Locked by', readOnly: true, inList: false, order: 7 }),
      ...sys(),
    ],
    actions: [
      act({ name: 'submit', label: 'Submit', when: { status: ['open'] }, icon: 'send' }),
      act({ name: 'approve', label: 'Approve', when: { status: ['submitted'] }, icon: 'check', tone: 'primary' }),
      act({ name: 'lock', label: 'Lock', tone: 'danger', icon: 'lock', when: { status: ['open', 'submitted', 'approved'] }, confirm: { title: 'Lock this timesheet?', body: 'Entries are frozen and prepay consumption is posted to the ledger. This cannot be undone.', confirmLabel: 'Lock timesheet' } }),
    ],
  }),

  'time-entries': base({
    name: 'time-entries',
    table: 'time_entry',
    label: 'Time entry',
    labelPlural: 'Time entries',
    icon: 'timer',
    capabilities: { list: true, get: true, create: true, update: true, remove: false },
    displayField: 'note',
    searchFields: ['note'],
    defaultSort: '-occurred_on',
    fields: [
      field({ name: 'occurred_on', type: 'date', label: 'Date', inCreate: true, inUpdate: true, order: 1 }),
      field({ name: 'technician_id', type: 'relation', label: 'Technician', required: true, nullable: false, inCreate: true, order: 2, relation: { resource: 'technicians', displayField: 'name' } }),
      field({ name: 'client_id', type: 'relation', label: 'Client', inCreate: true, inUpdate: true, order: 3, relation: { resource: 'clients', displayField: 'name' } }),
      field({ name: 'contract_id', type: 'relation', label: 'Contract', inCreate: true, inList: false, order: 4, relation: { resource: 'contracts', displayField: 'ref' } }),
      field({ name: 'minutes', type: 'duration', label: 'Time', required: true, nullable: false, inCreate: true, inUpdate: true, align: 'right', order: 5 }),
      field({ name: 'charge_type', type: 'enum', label: 'Charge', inCreate: true, inUpdate: true, order: 6, default: 'tm', enum: enumOf([['tm', 'Time & materials', 'info'], ['prepay', 'Prepaid balance', 'pos'], ['contract_included', 'Included in contract', 'warn'], ['non_billable', 'Non-billable', 'muted']]) }),
      field({ name: 'billable', type: 'boolean', label: 'Billable', inCreate: true, inUpdate: true, inList: false, order: 7 }),
      field({ name: 'note', type: 'textarea', label: 'Note', searchable: true, inCreate: true, inUpdate: true, order: 8, help: 'Becomes the invoice line' }),
      money('bill_rate_minor', 'Bill rate', { readOnly: true, inList: false, order: 9 }),
      money('cost_rate_minor', 'Cost rate', { readOnly: true, inList: false, order: 10 }),
      money('billable_amount_minor', 'Billable', { readOnly: true, order: 11 }),
      field({ name: 'currency', type: 'text', label: 'Currency', readOnly: true, inList: false, order: 12 }),
      field({ name: 'timesheet_id', type: 'relation', label: 'Timesheet', readOnly: true, inList: false, order: 13, relation: { resource: 'timesheets', displayField: 'period_start' } }),
      field({ name: 'locked_at', type: 'datetime', label: 'Locked', readOnly: true, inList: false, order: 14 }),
      ...sys(),
    ],
    actions: [act({ name: 'unbilled', label: 'Unbilled', scope: 'collection', method: 'GET', path: 'unbilled' })],
  }),

  'prepay-accounts': base({
    name: 'prepay-accounts',
    table: 'prepay_account',
    label: 'Prepay account',
    labelPlural: 'Prepay accounts',
    icon: 'wallet',
    capabilities: { list: true, get: true, create: true, update: false, remove: false },
    displayField: 'client_name',
    searchFields: ['client_name'],
    fields: [
      field({ name: 'client_name', type: 'text', label: 'Client', readOnly: true, sortable: false, filterable: false, order: 1 }),
      field({ name: 'contract_id', type: 'relation', label: 'Contract', required: true, nullable: false, inCreate: true, order: 2, relation: { resource: 'contracts', displayField: 'ref' } }),
      field({ name: 'unit', type: 'enum', label: 'Unit', inCreate: true, order: 3, default: 'hours', enum: enumOf([['hours', 'Hours'], ['money', 'Money']]) }),
      field({ name: 'currency', type: 'text', label: 'Currency', readOnly: true, order: 4 }),
      field({ name: 'status', type: 'text', label: 'Status', readOnly: true, order: 5 }),
      field({ name: 'expiry_months', type: 'integer', label: 'Expiry (months)', inCreate: true, order: 6, align: 'right' }),
      ...sys(),
    ],
    actions: [act({ name: 'top-up', label: 'Top up', tone: 'primary', icon: 'plus', input: { type: 'object', properties: { quantity: { type: 'number', title: 'Quantity (hours)' }, amount_minor: { type: 'integer', title: 'Amount (minor units)' }, occurred_on: { type: 'string', format: 'date', title: 'Date' }, note: { type: 'string', title: 'Note' } } } })],
  }),

  'ledger-accounts': base({
    name: 'ledger-accounts',
    table: 'ledger_account',
    label: 'Ledger account',
    labelPlural: 'Ledger accounts',
    icon: 'book-open',
    displayField: 'account_type',
    searchFields: ['party_name'],
    fields: [
      field({ name: 'account_type', type: 'text', label: 'Type', readOnly: true, order: 1 }),
      field({ name: 'party_name', type: 'text', label: 'Party', readOnly: true, sortable: false, filterable: false, order: 2 }),
      field({ name: 'currency', type: 'text', label: 'Currency', readOnly: true, order: 3 }),
      money('running_amount_minor', 'Balance', { readOnly: true, order: 4 }),
      field({ name: 'running_quantity', type: 'number', label: 'Quantity', readOnly: true, align: 'right', order: 5 }),
      field({ name: 'last_seq', type: 'integer', label: 'Last seq', readOnly: true, align: 'right', inList: false, order: 6 }),
      ...sys(),
    ],
    actions: [act({ name: 'verify', label: 'Verify chain', icon: 'shield-check' })],
  }),

  'recon-exceptions': base({
    name: 'recon-exceptions',
    table: 'recon_exception',
    label: 'Reconciliation exception',
    labelPlural: 'Reconciliation exceptions',
    icon: 'alert-triangle',
    displayField: 'kind',
    searchFields: ['kind', 'entity_type'],
    defaultSort: '-detected_at',
    fields: [
      field({ name: 'kind', type: 'text', label: 'Kind', readOnly: true, order: 1 }),
      field({ name: 'entity_type', type: 'text', label: 'Entity', readOnly: true, order: 2 }),
      field({ name: 'integration', type: 'text', label: 'Integration', readOnly: true, order: 3 }),
      field({ name: 'status', type: 'enum', label: 'Status', readOnly: true, order: 4, enum: enumOf([['open', 'Open', 'neg'], ['resolved', 'Resolved', 'pos'], ['ignored', 'Ignored', 'muted']]) }),
      field({ name: 'detected_at', type: 'datetime', label: 'Detected', readOnly: true, order: 5 }),
      field({ name: 'resolved_at', type: 'datetime', label: 'Resolved', readOnly: true, order: 6, inList: false }),
      field({ name: 'detail', type: 'json', label: 'Detail', readOnly: true, inList: false, order: 7 }),
      ...sys(),
    ],
    actions: [act({ name: 'resolve', label: 'Resolve', tone: 'primary', icon: 'check', when: { status: ['open'] }, input: { type: 'object', properties: { status: { type: 'string', enum: ['resolved', 'ignored'], title: 'Outcome', default: 'resolved' } }, required: ['status'] } })],
  }),
};

export function fallbackMeta(name: string): ResourceMeta | undefined {
  return FALLBACK_META[name];
}
